import { create } from 'zustand';

/** A single autopilot parameter. Mirrors MAVLink `PARAM_VALUE`. */
export interface ParamValue {
  id: string;
  value: number;
  /** MAVLink `MAV_PARAM_TYPE` discriminator (1..10). Hub re-encodes on write. */
  param_type: number;
  /** 0-based index into the autopilot's parameter table. Useful for
   *  showing "loaded 412 / 1368". */
  index: number;
  count: number;
  /** Hub-local epoch ms of last update. */
  updated_at_ms: number;
}

interface ParamsStore {
  /** Per-robot loaded params, keyed by robot_id then by param id. */
  byRobot: Record<string, Record<string, ParamValue>>;
  /** Per-robot total expected count (from `PARAM_VALUE.count`). 0 until first
   *  value arrives. */
  expected: Record<string, number>;
  upsertParam: (robotId: string, p: ParamValue) => void;
  clearRobot: (robotId: string) => void;
  /** Convenience getter: total loaded for a robot. */
  loaded: (robotId: string) => number;
}

export const useParams = create<ParamsStore>((set, get) => ({
  byRobot: {},
  expected: {},
  upsertParam: (robotId, p) =>
    set((s) => {
      const robot = { ...(s.byRobot[robotId] ?? {}) };
      robot[p.id] = p;
      return {
        byRobot: { ...s.byRobot, [robotId]: robot },
        expected: { ...s.expected, [robotId]: p.count || s.expected[robotId] || 0 },
      };
    }),
  clearRobot: (robotId) =>
    set((s) => {
      const robots = { ...s.byRobot };
      delete robots[robotId];
      const expected = { ...s.expected };
      delete expected[robotId];
      return { byRobot: robots, expected };
    }),
  loaded: (robotId) => {
    const r = get().byRobot[robotId];
    return r ? Object.keys(r).length : 0;
  },
}));

/** Coarse grouping by the ArduCopter param-name prefix convention.
 *  Common groups: ARMING, ATC, BATT, COMPASS, EKF, EK3, FRAME, FS, GPS, INS,
 *  MOT, RC, SR, FLTMODE, AUTO, BARO, MAG, etc. Returns "Other" for params
 *  whose prefix doesn't match a known group. */
export function paramGroup(name: string): string {
  const upper = name.toUpperCase();
  const groups = [
    'ARMING', 'ATC', 'AUTO', 'BARO', 'BATT', 'BRD',
    'COMPASS', 'EKF', 'EK3', 'FENCE', 'FLTMODE', 'FRAME',
    'FS', 'GPS', 'INS', 'LAND', 'LOG', 'MAG',
    'MIS', 'MOT', 'PILOT', 'RC', 'SCHED', 'SCR',
    'SERIAL', 'SR', 'TERRAIN', 'TUNE', 'WP', 'WPNAV',
  ];
  for (const g of groups) {
    if (upper.startsWith(g + '_') || upper === g) return g;
  }
  return 'Other';
}
